/**
 * @file        src/lib/frequentFlyer.js
 * @description A traveller's frequent-flyer memberships as a LIST — one per
 *              flight of the trip.
 *
 *              A VERBATIM port of the storefronts' src/lib/frequentFlyer.js, so
 *              the admin e-ticket, invoice and booking details print exactly what
 *              the customer's own copy prints. Keep the three in sync.
 *
 *              A booking made before per-flight capture carries only the single
 *              `ffAirline` / `ffNumber` pair; it is read as a one-entry list, so
 *              old tickets print exactly as they did.
 * @author      Kinetika Labs
 * @created     2026-09-11
 * @changelog
 *   - 2026-09-11 Claude — Initial (ported from booking_frontends/<storefront>).
 *   - 2026-09-29 Claude — Labels print the FF number only ("FF: 233777777"), no
 *     airline code — same change the storefronts took on 2026-09-28. The same
 *     airline + number on several flights prints once (one number per carrier).
 */

/** Normalised `{ route, airline, number }` list; entries without a number are dropped. */
export function ffEntries(t) {
  const list = (Array.isArray(t?.ffByFlight) ? t.ffByFlight : [])
    .map((e) => ({
      route: String(e?.route || '').trim(),
      airline: String(e?.airline || '').trim().toUpperCase(),
      number: String(e?.number || '').trim(),
    }))
    .filter((e) => e.number);
  if (list.length) return list;
  const number = String(t?.ffNumber || '').trim();
  const airline = String(t?.ffAirline || '').trim().toUpperCase();
  return number ? [{ route: '', airline, number }] : [];
}

/**
 * One printable label per membership — the NUMBER only ("12345678"; callers
 * prefix "FF:"). The airline code is not printed: it reads as part of the number.
 * The route is appended only when there is more than one, because that is the
 * only time a reader has to be told which flight a number belongs to.
 */
export function ffLabels(t) {
  // The airline holds ONE number per carrier and applies it to every flight it
  // operates, so the same airline + number on several flights is one membership:
  // print it once, naming all its flights. (A booking made before capture went
  // per-airline stored one row per flight.)
  const list = [];
  for (const e of ffEntries(t)) {
    const same = list.find((x) => x.airline === e.airline && x.number === e.number);
    if (same) { if (e.route) same.routes.push(e.route); }
    else list.push({ ...e, routes: e.route ? [e.route] : [] });
  }
  return list.map((e) => (list.length > 1 && e.routes.length ? `${e.number} (${e.routes.join(', ')})` : e.number));
}
